import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API_URL, getDefaultPmUiUrl, getWsBaseUrl, pmUiUrlChanged, resolvePmUiUrl } from '../config';
import {
  ensureIframeHooks,
  executeIframeCommand,
  probeIframe,
  type IframeConsoleEntry,
  type IframeNetEntry,
} from '../utils/iframeProbe';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

type ConsoleEntry = { ts?: string; type?: string; text?: string };
type NetEntry = {
  ts?: string;
  phase?: string;
  method?: string;
  url?: string;
  status?: number;
  ok?: boolean;
};

const BrowserPanel: React.FC<Props> = ({ isOpen, onClose }) => {
  const [branch, setBranch] = useState<string | null>(null);
  const [url, setUrl] = useState('');
  const initialPm = getDefaultPmUiUrl();
  const [previewUrl, setPreviewUrl] = useState(initialPm);
  const [iframeSrc, setIframeSrc] = useState<string | null>(initialPm);
  const [booted, setBooted] = useState(true);
  const [status, setStatus] = useState('idle');
  const [consoleLines, setConsoleLines] = useState<ConsoleEntry[]>([]);
  const [network, setNetwork] = useState<NetEntry[]>([]);
  const [tab, setTab] = useState<'view' | 'console' | 'network'>('view');
  const [busy, setBusy] = useState(false);
  const [iframeBlocked, setIframeBlocked] = useState(false);
  const [iframeReadable, setIframeReadable] = useState<boolean | null>(null);
  const [iframeUrl, setIframeUrl] = useState('');
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const sinksRef = useRef<{
    console: IframeConsoleEntry[];
    network: IframeNetEntry[];
  }>({ console: [], network: [] });
  const iframeReadableRef = useRef<boolean | null>(null);
  const lastSrcRef = useRef<string | null>(initialPm);
  const agentStartedRef = useRef(false);

  const syncDevtoolsFromSinks = useCallback(() => {
    setConsoleLines([...sinksRef.current.console]);
    setNetwork([...sinksRef.current.network]);
  }, []);

  const commitPreviewUrl = useCallback((next: string) => {
    const resolved = resolvePmUiUrl(next);
    if (!resolved) return;
    setPreviewUrl(resolved);
    // Soft-boot: only change iframe src when URL meaningfully changes
    if (!pmUiUrlChanged(lastSrcRef.current, resolved)) return;
    lastSrcRef.current = resolved;
    setIframeSrc(resolved);
    setBooted(true);
    setIframeBlocked(false);
    sinksRef.current = { console: [], network: [] };
    setConsoleLines([]);
    setNetwork([]);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/workspace/active`);
      const ws = res.data.workspace;
      const br = res.data.browser;
      setBranch(ws?.branch || null);
      const preview = resolvePmUiUrl(
        ws?.preview_url ||
          res.data.pm_ui_url_default ||
          res.data.pm_preview_url ||
          getDefaultPmUiUrl()
      );
      commitPreviewUrl(preview);
      setUrl(resolvePmUiUrl(br?.url || preview));
      setStatus(br?.status || 'idle');

      // Merge Playwright buffers when iframe hooks are not available
      if (!iframeReadableRef.current) {
        if (Array.isArray(br?.console) && br.console.length) {
          setConsoleLines(br.console);
        }
        if (Array.isArray(br?.network) && br.network.length) {
          setNetwork(br.network);
        }
      } else {
        syncDevtoolsFromSinks();
      }
    } catch (e) {
      console.error(e);
      commitPreviewUrl(getDefaultPmUiUrl());
    }
  }, [commitPreviewUrl, syncDevtoolsFromSinks]);

  const pushSnapshot = useCallback(async () => {
    const probe = probeIframe(iframeRef.current, sinksRef.current);
    iframeReadableRef.current = probe.readable;
    setIframeReadable(probe.readable);
    if (probe.readable) {
      setIframeUrl(probe.url);
      syncDevtoolsFromSinks();
    }
    try {
      await axios.post(`${API_URL}/api/workspace/iframe-snapshot`, probe);
    } catch {
      /* ignore */
    }
  }, [syncDevtoolsFromSinks]);

  // Soft-boot once on mount (even before panel opens) so first open is instant
  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // WebSocket for agent console/network + iframe commands (stable; don't reconnect on readable)
  useEffect(() => {
    if (!isOpen) return;
    const token = localStorage.getItem('access_token');
    let ws: WebSocket | null = null;
    let cancelled = false;
    (async () => {
      try {
        const me = await axios.get(`${API_URL}/api/auth/me`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (cancelled) return;
        const uid = me.data.id;
        ws = new WebSocket(`${getWsBaseUrl()}/ws/browser-${uid}`);
        ws.onmessage = (ev) => {
          try {
            const data = JSON.parse(ev.data);
            if (data.type === 'iframe_command' && data.id) {
              (async () => {
                const result = await executeIframeCommand(iframeRef.current, data);
                try {
                  await axios.post(`${API_URL}/api/workspace/iframe-command-result`, {
                    id: data.id,
                    ...result,
                  });
                } catch (e) {
                  console.warn('iframe command result failed', e);
                }
                await pushSnapshot();
              })();
              return;
            }
            if (data.type === 'browser_console' && data.data) {
              // Always keep agent console (cross-origin iframe can't hook)
              setConsoleLines((prev) => {
                const next = [...prev.slice(-199), data.data];
                return next;
              });
              pushCapSinkConsole(sinksRef.current.console, data.data);
            } else if (data.type === 'browser_network' && data.data) {
              setNetwork((prev) => [...prev.slice(-199), data.data]);
              pushCapSinkNet(sinksRef.current.network, data.data);
            } else if (data.type === 'browser_status' && data.data) {
              setStatus(data.data.status || 'idle');
              if (data.data.url) setUrl(data.data.url);
              if (data.data.branch) setBranch(data.data.branch);
            }
          } catch {
            /* ignore */
          }
        };
      } catch (e) {
        console.error(e);
      }
    })();
    return () => {
      cancelled = true;
      ws?.close();
    };
  }, [isOpen, pushSnapshot]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (ev: Event) => {
      const data = (ev as CustomEvent).detail;
      if (!data?.id) return;
      (async () => {
        const result = await executeIframeCommand(iframeRef.current, data);
        try {
          await axios.post(`${API_URL}/api/workspace/iframe-command-result`, {
            id: data.id,
            ...result,
          });
        } catch (e) {
          console.warn('iframe command result failed', e);
        }
        pushSnapshot();
      })();
    };
    window.addEventListener('complic-iframe-command', handler as EventListener);
    return () => window.removeEventListener('complic-iframe-command', handler as EventListener);
  }, [isOpen, pushSnapshot]);

  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [isOpen, onClose]);

  // Poll iframe probe only while open (does not remount iframe)
  useEffect(() => {
    if (!isOpen || !booted) return;
    pushSnapshot();
    const id = window.setInterval(pushSnapshot, 2000);
    return () => window.clearInterval(id);
  }, [isOpen, booted, pushSnapshot]);

  // Start Playwright agent once per panel session so console/network get errors cross-origin
  useEffect(() => {
    if (!isOpen || !previewUrl) return;
    if (agentStartedRef.current) return;
    if (status === 'running' || status === 'starting') {
      agentStartedRef.current = true;
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setBusy(true);
        await axios.post(`${API_URL}/api/workspace/browser/start`);
        agentStartedRef.current = true;
        if (!cancelled) await refresh();
      } catch (e) {
        console.warn('agent browser start failed', e);
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, previewUrl]);

  // Reset agent-start gate when panel fully closes so next open can restart if needed
  useEffect(() => {
    if (!isOpen) {
      // keep iframe alive; only allow agent restart next open if it died
      if (status === 'idle' || status === 'error' || status === 'stopped') {
        agentStartedRef.current = false;
      }
    }
  }, [isOpen, status]);

  const stopBrowser = async () => {
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/workspace/browser/stop`);
      agentStartedRef.current = false;
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const startBrowser = async () => {
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/workspace/browser/start`);
      agentStartedRef.current = true;
      await refresh();
    } catch (e: any) {
      window.alert(e?.response?.data?.detail || e.message || 'Start failed');
    } finally {
      setBusy(false);
    }
  };

  const openExternal = () => {
    const target = iframeUrl || previewUrl || url;
    if (target) window.open(target, '_blank', 'noopener,noreferrer');
  };

  const displayUrl = iframeUrl || previewUrl || url;

  const onIframeLoad = () => {
    // Don't wipe sinks on every soft navigation inside SPA — only re-hook
    if (iframeRef.current) {
      ensureIframeHooks(iframeRef.current, sinksRef.current);
    }
    pushSnapshot();
  };

  const errorConsole = consoleLines.filter(
    (l) => l.type === 'error' || l.type === 'warn' || /error|failed|exception/i.test(l.text || '')
  );
  const errorNetwork = network.filter(
    (n) => (n.phase === 'response' || n.status != null) && (n.ok === false || (n.status != null && n.status >= 400))
  );
  const allNetwork = network.filter((n) => n.phase === 'response' || n.status != null);

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={onClose} />
      )}
      <div
        className={`
          fixed top-0 left-0 h-screen bg-gray-900/95 z-50 backdrop-blur-sm
          transition-transform duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          ${isOpen ? 'pointer-events-auto' : 'pointer-events-none'}
          w-full lg:w-2/3 border-r border-emerald-400/40
          shadow-[0_0_50px_rgba(16,185,129,0.25)]
          flex flex-col
        `}
        aria-hidden={!isOpen}
      >
        <div className="px-3 py-2 border-b border-emerald-400/30 flex items-center gap-2 text-[11px] font-mono text-emerald-100">
          <span className="font-sans font-semibold text-xs text-white">PM UI</span>
          <span className="truncate opacity-80 max-w-[28%]">{branch || 'live PM'}</span>
          <span className="opacity-50">·</span>
          <span className="truncate flex-1 opacity-70">{displayUrl || '—'}</span>
          <span
            className={`px-1.5 py-0.5 rounded ${
              iframeReadable
                ? 'bg-emerald-500/20 text-emerald-300'
                : iframeReadable === false
                  ? 'bg-amber-500/20 text-amber-300'
                  : 'bg-gray-500/20 text-gray-300'
            }`}
            title={
              iframeReadable
                ? 'Iframe readable — Complic sees your PM session'
                : 'Cross-origin blocked — console/network from agent Playwright'
            }
          >
            {iframeReadable ? 'live' : iframeReadable === false ? 'blocked' : '…'}
          </span>
          <span
            className={`px-1.5 py-0.5 rounded ${
              status === 'running'
                ? 'bg-emerald-500/20 text-emerald-300'
                : status === 'error'
                  ? 'bg-red-500/20 text-red-300'
                  : 'bg-gray-500/20 text-gray-300'
            }`}
            title="Agent Playwright session"
          >
            agent:{status}
          </span>
          {(errorConsole.length > 0 || errorNetwork.length > 0) && (
            <span
              className="px-1.5 py-0.5 rounded bg-red-500/25 text-red-200 cursor-pointer"
              title="Open console / network errors"
              onClick={() => setTab(errorConsole.length ? 'console' : 'network')}
            >
              {errorConsole.length + errorNetwork.length} err
            </span>
          )}
          <button
            type="button"
            onClick={openExternal}
            disabled={!displayUrl}
            className="px-2 py-1 rounded bg-emerald-600 text-white text-xs font-sans disabled:opacity-40"
            tabIndex={isOpen ? 0 : -1}
          >
            Open
          </button>
          {status === 'running' ? (
            <button
              type="button"
              disabled={busy}
              onClick={stopBrowser}
              className="px-2 py-1 rounded bg-gray-700 text-white text-xs font-sans"
              tabIndex={isOpen ? 0 : -1}
            >
              Stop agent
            </button>
          ) : (
            <button
              type="button"
              disabled={busy}
              onClick={startBrowser}
              className="px-2 py-1 rounded bg-gray-700 text-white text-xs font-sans disabled:opacity-40"
              tabIndex={isOpen ? 0 : -1}
            >
              Start agent
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-white px-1"
            tabIndex={isOpen ? 0 : -1}
          >
            ✕
          </button>
        </div>

        {iframeReadable === false && (
          <div className="px-3 py-1.5 bg-amber-950/80 border-b border-amber-500/30 text-[11px] font-sans text-amber-100">
            Cross-origin: console/network bereme z Playwright agenta (auto-start). Pro přímý přístup
            do iframe:{' '}
            <code className="text-amber-200">./scripts/open-complic-chrome.sh</code>
          </div>
        )}

        <div className="flex gap-1 px-3 py-1 border-b border-gray-700 text-xs font-sans">
          {(['view', 'console', 'network'] as const).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={`px-2 py-1 rounded capitalize ${
                tab === t ? 'bg-emerald-500/20 text-emerald-200' : 'text-gray-400'
              }`}
              tabIndex={isOpen ? 0 : -1}
            >
              {t}
              {t === 'console' && errorConsole.length > 0 && (
                <span className="ml-1 text-red-300">{errorConsole.length}</span>
              )}
              {t === 'network' && errorNetwork.length > 0 && (
                <span className="ml-1 text-red-300">{errorNetwork.length}</span>
              )}
            </button>
          ))}
          {(tab === 'console' || tab === 'network') && (
            <span className="ml-auto text-[10px] text-gray-500 self-center">
              {iframeReadable ? 'from your PM iframe' : 'from agent Playwright'}
            </span>
          )}
        </div>

        <div className="flex-1 min-h-0 overflow-hidden bg-black relative">
          {/* Iframe stays mounted forever after soft-boot — tabs only hide it */}
          <div
            className={`absolute inset-0 bg-slate-100 ${
              tab === 'view' ? 'z-10' : 'z-0 invisible pointer-events-none'
            }`}
            aria-hidden={tab !== 'view'}
          >
            {!booted || !iframeSrc ? (
              <div className="w-full h-full flex items-center justify-center bg-gray-950">
                <p className="text-sm text-gray-400 font-sans px-6 text-center">
                  Loading PM UI…
                </p>
              </div>
            ) : iframeBlocked ? (
              <div className="w-full h-full flex flex-col items-center justify-center gap-3 bg-gray-950 px-6">
                <p className="text-sm text-gray-300 font-sans text-center">
                  Page cannot be embedded (X-Frame-Options). Open it in a normal browser tab.
                </p>
                <button
                  type="button"
                  onClick={openExternal}
                  className="px-3 py-2 rounded bg-emerald-600 text-white text-sm font-sans"
                >
                  Open {previewUrl || url}
                </button>
              </div>
            ) : (
              <iframe
                ref={iframeRef}
                title="PM UI"
                src={iframeSrc}
                className="w-full h-full border-0 bg-white"
                onLoad={onIframeLoad}
                onError={() => setIframeBlocked(true)}
                allow="clipboard-read; clipboard-write"
              />
            )}
          </div>

          {tab === 'console' && (
            <div className="absolute inset-0 z-20 h-full overflow-y-auto p-3 font-mono text-[11px] text-gray-200 space-y-1 bg-black">
              {consoleLines.length === 0 ? (
                <p className="text-gray-500">
                  No console yet.{' '}
                  {status !== 'running'
                    ? 'Start agent (auto) to capture errors cross-origin.'
                    : 'Waiting for logs…'}
                </p>
              ) : (
                <>
                  {errorConsole.length > 0 && (
                    <div className="mb-2 pb-2 border-b border-red-500/30">
                      <p className="text-red-300 text-[10px] uppercase tracking-wide mb-1">
                        Errors / warnings ({errorConsole.length})
                      </p>
                      {errorConsole.map((l, i) => (
                        <div key={`e-${i}`} className="text-red-300">
                          <span className="text-gray-500">[{l.type}] </span>
                          {l.text}
                        </div>
                      ))}
                    </div>
                  )}
                  {consoleLines.map((l, i) => (
                    <div
                      key={i}
                      className={
                        l.type === 'error'
                          ? 'text-red-300'
                          : l.type === 'warn'
                            ? 'text-amber-300'
                            : ''
                      }
                    >
                      <span className="text-gray-500">[{l.type}] </span>
                      {l.text}
                    </div>
                  ))}
                </>
              )}
            </div>
          )}

          {tab === 'network' && (
            <div className="absolute inset-0 z-20 h-full overflow-y-auto p-3 font-mono text-[11px] text-gray-200 space-y-1 bg-black">
              {allNetwork.length === 0 ? (
                <p className="text-gray-500">
                  No network yet.{' '}
                  {status !== 'running'
                    ? 'Start agent (auto) to capture failed requests.'
                    : 'Waiting for requests…'}
                </p>
              ) : (
                <>
                  {errorNetwork.length > 0 && (
                    <div className="mb-2 pb-2 border-b border-red-500/30">
                      <p className="text-red-300 text-[10px] uppercase tracking-wide mb-1">
                        Failed ({errorNetwork.length})
                      </p>
                      {errorNetwork.map((n, i) => (
                        <div key={`ne-${i}`} className="text-red-300">
                          {n.status} {n.method} {n.url}
                        </div>
                      ))}
                    </div>
                  )}
                  {allNetwork.map((n, i) => (
                    <div key={i} className={n.ok === false || (n.status != null && n.status >= 400) ? 'text-red-300' : 'text-gray-300'}>
                      {n.status} {n.method} {n.url}
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

function pushCapSinkConsole(arr: IframeConsoleEntry[], item: ConsoleEntry) {
  arr.push({
    ts: item.ts || new Date().toISOString(),
    type: item.type || 'log',
    text: item.text || '',
  });
  if (arr.length > 100) arr.splice(0, arr.length - 100);
}

function pushCapSinkNet(arr: IframeNetEntry[], item: NetEntry) {
  arr.push({
    ts: item.ts || new Date().toISOString(),
    phase: item.phase,
    method: item.method,
    url: item.url,
    status: item.status,
    ok: item.ok,
  });
  if (arr.length > 100) arr.splice(0, arr.length - 100);
}

export default BrowserPanel;
